import React, { useState, useCallback } from 'react'
import { useVisitors, Visitor } from '../../hooks/useVisitors'
import { useNotifications } from '../../contexts/NotificationContext'
import { apiPost, getErrorMessage } from '../../utils/api'
import { VisitorMapView } from './VisitorMapView'
import { VisitorDetailModal } from './VisitorDetailModal'
import type { MapVisitor } from './visitorMap.helpers'
// formatDate and formatDuration available from '../../utils/format' if needed

/* ─── BUG-06: Error Boundary ────────────────────────────────────────────── */

class VisitorErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  state = { hasError: false, error: null as Error | null }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-full p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-error/10 flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-error" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
            </svg>
          </div>
          <h2 className="text-lg font-bold text-txt-primary mb-2">Something went wrong</h2>
          <p className="text-sm text-txt-secondary mb-4">The visitors page encountered an error.</p>
          <button
            onClick={() => this.setState({ hasError: false, error: null })}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-brand-600 text-white hover:bg-brand-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

/* ─── Icons ────────────────────────────────────────────────────────────── */

const EyeIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
)

const OnlineIcon = () => (
  <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 8 8">
    <circle cx="4" cy="4" r="4" />
  </svg>
)

const GlobeIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.75 9.75 0 100-19.5A9.75 9.75 0 0012 21z" />
  </svg>
)

const MessageIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
  </svg>
)

const ClockIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
)

const RefreshIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" />
  </svg>
)

/* Hosts that count as "in the WeQuote app right now". Kept narrow so the
 * marketing site / docs don't trigger it. */
const WEQUOTE_APP_HOSTS = ['app.wequote.cloud', 'staging.wequote.cloud']
function isInWeQuoteApp(url?: string | null): boolean {
  if (!url) return false
  try {
    const host = new URL(url).hostname.toLowerCase()
    return WEQUOTE_APP_HOSTS.includes(host)
  } catch {
    // Not an absolute URL — fall back to a substring check.
    const u = url.toLowerCase()
    return WEQUOTE_APP_HOSTS.some((h) => u.includes(h))
  }
}

/* ─── Component ────────────────────────────────────────────────────────── */

const VisitorsPageInner: React.FC = () => {
  const { visitors, stats, loading, filter, setFilter, sortBy, setSortBy, refresh } = useVisitors()
  const { addNotification } = useNotifications()
  const [startingChat, setStartingChat] = useState<string | null>(null)
  const [detailVisitor, setDetailVisitor] = useState<Visitor | null>(null)

  const handleStartChat = useCallback(async (visitorId: string) => {
    setStartingChat(visitorId)
    try {
      await apiPost(`/visitors/${visitorId}/proactive-chat`, {})
      addNotification('Chat initiated successfully', 'success')
    } catch (err) {
      addNotification(
        getErrorMessage(err, 'Failed to start chat'),
        'error'
      )
    } finally {
      setStartingChat(null)
    }
  }, [addNotification])

  const getLeadScoreColor = (score: number) => {
    if (score < 30) return 'text-error bg-error/10'
    if (score < 60) return 'text-warning bg-warning/10'
    return 'text-success bg-success/10'
  }

  const getEngagementColor = (score: number) => {
    if (score < 30) return 'bg-error'
    if (score < 60) return 'bg-warning'
    return 'bg-success'
  }

  const getSourceBadgeColor = (source: string) => {
    const lowerSource = source.toLowerCase()
    if (lowerSource.includes('google') || lowerSource === 'organic') return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
    if (lowerSource.includes('facebook') || lowerSource.includes('instagram') || lowerSource.includes('twitter') || lowerSource === 'social') return 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
    if (lowerSource.includes('email')) return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
    if (lowerSource === 'direct') return 'bg-slate-100 dark:bg-slate-900/30 text-slate-700 dark:text-slate-300'
    return 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
  }

  // BUG-13: Properly typed parameter instead of 'any'
  const formatVisitorName = (visitor: Visitor) => {
    // Prefer the captured visitor name (from the widget identity), then company,
    // then the account label, then anonymous.
    const name = (visitor as any).visitor_name
    if (name) return name
    const company = (visitor as any).company_name
    if (company) return company
    const acct = (visitor as any).wequote_account_id || (visitor as any).account_alias
    if (acct) return `Account ${acct}`
    if (visitor.customer_id) return `Known visitor`
    return `Anonymous · ${visitor.fingerprint?.slice(0, 6) || '—'}`
  }

  const formatTimeOnSite = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`
    const minutes = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${minutes}m ${secs}s`
  }

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="flex flex-col items-center gap-3">
          <svg className="w-8 h-8 text-brand-500 animate-spin-slow" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <span className="text-sm text-txt-secondary">Loading visitors...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-xl font-bold text-txt-primary">Visitors</h1>
            <p className="text-sm text-txt-secondary mt-0.5">Real-time visitor tracking and engagement</p>
          </div>

          <button
            onClick={() => refresh()}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium bg-brand-600 text-white hover:bg-brand-700 transition-colors"
            title="Refresh"
          >
            <RefreshIcon />
            Refresh
          </button>
        </div>

        {/* KPI Cards */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
            {/* Online Now */}
            <div className="p-4 rounded-xl bg-surface border border-border">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-txt-tertiary">Online Now</span>
                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-success/20 text-success">
                  <OnlineIcon />
                </div>
              </div>
              <p className="text-2xl font-bold text-txt-primary">{stats.online_now}</p>
              <p className="text-[11px] text-txt-tertiary mt-1">currently active</p>
            </div>

            {/* Total Visitors */}
            <div className="p-4 rounded-xl bg-surface border border-border">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-txt-tertiary">Total Visitors</span>
                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-brand-600/20 text-brand-600">
                  <EyeIcon />
                </div>
              </div>
              <p className="text-2xl font-bold text-txt-primary">{stats.total_visitors}</p>
              <p className="text-[11px] text-txt-tertiary mt-1">all time</p>
            </div>

            {/* New Today */}
            <div className="p-4 rounded-xl bg-surface border border-border">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-txt-tertiary">New Today</span>
                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-info-light text-info">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m6-3H6" />
                  </svg>
                </div>
              </div>
              <p className="text-2xl font-bold text-txt-primary">{stats.new_today}</p>
              <p className="text-[11px] text-txt-tertiary mt-1">first visit</p>
            </div>

            {/* Returning Today */}
            <div className="p-4 rounded-xl bg-surface border border-border">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-txt-tertiary">Returning</span>
                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </div>
              </div>
              <p className="text-2xl font-bold text-txt-primary">{stats.returning_today}</p>
              <p className="text-[11px] text-txt-tertiary mt-1">repeat visits</p>
            </div>

            {/* Avg Lead Score */}
            <div className="p-4 rounded-xl bg-surface border border-border">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-txt-tertiary">Avg Lead Score</span>
                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
              </div>
              <p className="text-2xl font-bold text-txt-primary">{stats.avg_lead_score.toFixed(1)}</p>
              <p className="text-[11px] text-txt-tertiary mt-1">engagement level</p>
            </div>

            {/* Avg Time on Site */}
            <div className="p-4 rounded-xl bg-surface border border-border">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-txt-tertiary">Avg Time</span>
                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400">
                  <ClockIcon />
                </div>
              </div>
              <p className="text-2xl font-bold text-txt-primary">{formatTimeOnSite(Math.floor(stats.avg_time_on_site))}</p>
              <p className="text-[11px] text-txt-tertiary mt-1">per session</p>
            </div>
          </div>
        )}

        {/* Filters and Sort */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center gap-3">
          {/* BUG-08: Added aria-pressed for accessibility */}
          <div className="flex gap-2" role="group" aria-label="Visitor filter">
            <button
              aria-pressed={filter === 'all'}
              onClick={() => setFilter('all')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                filter === 'all'
                  ? 'bg-brand-600 text-white ring-2 ring-brand-600/30'
                  : 'bg-surface border border-border text-txt-secondary hover:text-txt-primary'
              }`}
            >
              All
            </button>
            <button
              aria-pressed={filter === 'online'}
              onClick={() => setFilter('online')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                filter === 'online'
                  ? 'bg-success text-white ring-2 ring-success/30'
                  : 'bg-surface border border-border text-txt-secondary hover:text-txt-primary'
              }`}
            >
              Online
            </button>
            <button
              aria-pressed={filter === 'offline'}
              onClick={() => setFilter('offline')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                filter === 'offline'
                  ? 'bg-txt-tertiary text-white ring-2 ring-txt-tertiary/30'
                  : 'bg-surface border border-border text-txt-secondary hover:text-txt-primary'
              }`}
            >
              Offline
            </button>
          </div>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-1.5 rounded-lg text-sm font-medium bg-surface border border-border text-txt-primary outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500/20 transition-colors"
          >
            <option value="recent">Recent</option>
            <option value="lead_score">Lead Score</option>
            <option value="time">Time on Site</option>
            <option value="pages">Pages Viewed</option>
          </select>
        </div>

        {/* Visitors Table */}
        {visitors.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center px-6 rounded-xl bg-surface border border-border">
            <EyeIcon />
            <p className="text-sm font-medium text-txt-secondary mt-3">No visitors found</p>
            <p className="text-xs text-txt-tertiary mt-1">Visitors will appear here as they browse your site</p>
          </div>
        ) : (
          <div className="rounded-xl bg-surface border border-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-[10px] uppercase tracking-wide text-txt-tertiary">
                    <th className="text-left font-semibold px-4 py-2.5">Visitor</th>
                    <th className="text-left font-semibold px-4 py-2.5 hidden md:table-cell">Device</th>
                    <th className="text-right font-semibold px-4 py-2.5 w-20">Pages</th>
                    <th className="text-right font-semibold px-4 py-2.5 w-24">Time</th>
                    <th className="text-center font-semibold px-4 py-2.5 w-20">Lead</th>
                    <th className="text-left font-semibold px-4 py-2.5 w-28 hidden lg:table-cell">Status</th>
                    <th className="text-left font-semibold px-4 py-2.5 hidden lg:table-cell">Source</th>
                    <th className="px-4 py-2.5 w-44" />
                  </tr>
                </thead>
                <tbody>
                  {visitors.map((visitor) => (
                    <tr key={visitor.id} className="border-b border-border last:border-0 hover:bg-surface-hover transition-colors">
                      {/* Visitor */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <span className={`flex items-center justify-center w-7 h-7 rounded-full shrink-0 ${visitor.is_online ? 'bg-success/15' : 'bg-txt-tertiary/15'}`} title={visitor.is_online ? 'Online' : 'Offline'}>
                            <span className={`w-2 h-2 rounded-full ${visitor.is_online ? 'bg-success animate-pulse' : 'bg-txt-tertiary'}`} />
                          </span>
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5 min-w-0">
                              <button
                                type="button"
                                onClick={() => setDetailVisitor(visitor)}
                                className="font-semibold text-txt-primary truncate text-left hover:text-brand-600 hover:underline transition-colors cursor-pointer"
                                title="View visitor details"
                              >
                                {formatVisitorName(visitor)}
                              </button>
                              {visitor.is_online && isInWeQuoteApp((visitor as any).current_page_url) && (
                                <span
                                  className="inline-flex items-center gap-1 shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-success/15 text-success"
                                  title={(visitor as any).current_page_url || 'In the WeQuote app'}
                                >
                                  <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
                                  In WeQuote now
                                </span>
                              )}
                            </div>
                            <p className="text-[11px] text-txt-tertiary truncate">{[visitor.city, visitor.country].filter(Boolean).join(', ') || 'Location unknown'}</p>
                          </div>
                        </div>
                      </td>
                      {/* Device */}
                      <td className="px-4 py-3 hidden md:table-cell">
                        <p className="text-xs text-txt-secondary truncate max-w-[180px]">{visitor.browser ? `${visitor.browser}${visitor.os ? ` · ${visitor.os}` : ''}` : 'Unknown'}</p>
                      </td>
                      {/* Pages */}
                      <td className="px-4 py-3 text-right tabular-nums font-semibold text-txt-primary">{visitor.page_count || visitor.visit_count || 0}</td>
                      {/* Time */}
                      <td className="px-4 py-3 text-right tabular-nums text-txt-primary">{formatTimeOnSite(Math.floor(visitor.total_time_seconds || 0))}</td>
                      {/* Lead */}
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-bold ${getLeadScoreColor(visitor.lead_score)}`} title={`Lead score ${Math.round(visitor.lead_score)}/100`}>
                          {Math.round(visitor.lead_score)}
                          <span className="text-[9px] font-medium uppercase">{visitor.lead_score >= 60 ? 'Hi' : visitor.lead_score >= 30 ? 'Md' : 'Lo'}</span>
                        </span>
                      </td>
                      {/* Status */}
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-txt-secondary">
                          <span className={`w-2 h-2 rounded-full ${getEngagementColor(visitor.lead_score)}`} />
                          {visitor.lead_score >= 60 ? 'Active' : visitor.lead_score >= 30 ? 'Moderate' : 'Idle'}
                        </span>
                      </td>
                      {/* Source */}
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <div className="flex items-center gap-1.5">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">{visitor.visitor_type === 'new' ? 'New' : 'Returning'}</span>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${getSourceBadgeColor(visitor.referral_source)}`}>{(visitor.referral_source || 'direct').slice(0, 10)}</span>
                        </div>
                      </td>
                      {/* CTA */}
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => setDetailVisitor(visitor)}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-surface border border-border text-txt-secondary hover:text-txt-primary hover:bg-surface-hover transition-colors"
                            title="View visitor details"
                          >
                            <EyeIcon />
                            Details
                          </button>
                          <button
                            onClick={() => handleStartChat(visitor.id)}
                            disabled={startingChat === visitor.id || !visitor.is_online}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            title={visitor.is_online ? 'Start a chat with this visitor' : 'Visitor is offline'}
                          >
                            <MessageIcon />
                            {startingChat === visitor.id ? '…' : 'Chat'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {detailVisitor && (
        <VisitorDetailModal
          visitor={detailVisitor as unknown as MapVisitor}
          onClose={() => setDetailVisitor(null)}
        />
      )}
    </div>
  )
}

/* ─── Map / List tab wrapper ───────────────────────────────────────────── */

const MapIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 6.75V15m6-6v8.25m.503 3.498l4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 00-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0z" />
  </svg>
)

const ListIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 5.25h16.5m-16.5 4.5h16.5m-16.5 4.5h16.5m-16.5 4.5h16.5" />
  </svg>
)

const VisitorsTabs: React.FC = () => {
  const [tab, setTab] = useState<'map' | 'list'>('map')

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Tab bar */}
      <div className="flex items-center gap-1 px-4 pt-3 pb-2 border-b border-border bg-surface shrink-0">
        <button
          aria-pressed={tab === 'map'}
          onClick={() => setTab('map')}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            tab === 'map'
              ? 'bg-brand-600 text-white'
              : 'text-txt-secondary hover:text-txt-primary hover:bg-surface-hover'
          }`}
        >
          <MapIcon />
          Map
        </button>
        <button
          aria-pressed={tab === 'list'}
          onClick={() => setTab('list')}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            tab === 'list'
              ? 'bg-brand-600 text-white'
              : 'text-txt-secondary hover:text-txt-primary hover:bg-surface-hover'
          }`}
        >
          <ListIcon />
          List
        </button>
      </div>

      <div className="flex-1 min-h-0">
        {tab === 'map' ? <VisitorMapView /> : <VisitorsPageInner />}
      </div>
    </div>
  )
}

// BUG-06: Wrap with error boundary to prevent white-screen crashes
export const VisitorsPage: React.FC = () => (
  <VisitorErrorBoundary>
    <VisitorsTabs />
  </VisitorErrorBoundary>
)
